<template>
    <Teleport to="#neo-aside-header-actions">
        <div class="inline-flex bg-neutral-100 dark:bg-gray-800 rounded-full p-1 gap-0.5">
            <button
                type="button"
                class="px-4 py-1.5 rounded-full text-sm font-semibold transition"
                :class="activeTab === 'client'
                    ? 'bg-white dark:bg-gray-700 text-neutral-800 dark:text-neutral-100 shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'"
                @click="activeTab = 'client'"
            >{{ $t('contacts.tab_client') }}</button>
            <button
                type="button"
                class="px-4 py-1.5 rounded-full text-sm font-semibold transition"
                :class="activeTab === 'notebook'
                    ? 'bg-white dark:bg-gray-700 text-neutral-800 dark:text-neutral-100 shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'"
                @click="activeTab = 'notebook'"
            >{{ $t('contacts.tab_notebook') }}</button>
        </div>
    </Teleport>

    <div class="h-full flex flex-col min-h-0">
        <Tabs v-model:value="activeTab" class="h-full flex flex-col min-h-0">
            <TabPanels class="!p-0 flex-1 min-h-0 flex flex-col">
                <TabPanel value="client" class="!p-0 flex-1 min-h-0 flex flex-col">
                    <div class="flex-1 min-h-0 flex flex-col md:flex-row md:divide-x md:divide-neutral-200 md:dark:divide-gray-700">
                        <!-- LEVÁ STRANA: základní informace -->
                        <div class="md:w-[300px] md:flex-shrink-0 p-3 md:p-4 flex flex-col gap-3.5 overflow-y-auto">
                            <div class="flex flex-col gap-0.5">
                                <div v-if="contact.previous_names?.length" class="flex items-start gap-2.5 px-2.5 py-1.5">
                                    <i class="pi pi-history !text-sm text-neutral-400 flex-shrink-0 mt-0.5"></i>
                                    <p class="flex-1 text-[13.5px] text-neutral-500 dark:text-neutral-400 leading-relaxed">
                                        {{ $t('contacts.aka_prefix') }}
                                        <template v-for="(name, i) in visiblePreviousNames" :key="name">
                                            <b class="text-neutral-700 dark:text-neutral-200 font-semibold">{{ name }}</b><span v-if="i < visiblePreviousNames.length - 1">, </span>
                                        </template>
                                        <button
                                            v-if="contact.previous_names.length > 5"
                                            type="button"
                                            class="ml-1 text-neutral-400 hover:text-roori-blue underline underline-offset-2"
                                            @click="previousNamesExpanded = !previousNamesExpanded"
                                        >{{ previousNamesExpanded ? $t('common.show_less') : $t('common.show_all') }}</button>
                                    </p>
                                </div>
                                <div class="group flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-gray-800/60">
                                    <i class="pi pi-user !text-sm text-neutral-400 flex-shrink-0"></i>
                                    <FormInput
                                        v-if="editingField === 'name'"
                                        v-model="form.name"
                                        type="text"
                                        label="d"
                                        hide-label
                                        class="flex-1"
                                        @keyup.enter="editingField = null"
                                    />
                                    <span
                                        v-else
                                        class="flex-1 text-[13.5px] text-neutral-800 dark:text-neutral-100 cursor-pointer truncate"
                                        @click="editingField = 'name'"
                                    >{{ form.name || '—' }}</span>
                                </div>
                                <div class="group flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-gray-800/60">
                                    <i class="pi pi-envelope !text-sm text-neutral-400 flex-shrink-0"></i>
                                    <span class="flex-1 text-[13.5px] text-neutral-600 dark:text-neutral-300 truncate">{{ form.email || '—' }}</span>
                                    <a
                                        v-if="form.email"
                                        :href="'mailto:' + form.email"
                                        class="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-neutral-400 hover:text-roori-blue hover:bg-roori-blue/10 transition flex-shrink-0"
                                    >
                                        <i class="pi pi-arrow-up-right !text-xs"></i>
                                    </a>
                                </div>
                                <div class="group flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-gray-800/60">
                                    <i class="pi pi-phone !text-sm text-neutral-400 flex-shrink-0"></i>
                                    <FormInput
                                        v-if="editingField === 'phone'"
                                        v-model="form.phone"
                                        type="phone"
                                        label="d"
                                        hide-label
                                        class="flex-1"
                                    />
                                    <span
                                        v-else
                                        class="flex-1 text-[13.5px] text-neutral-600 dark:text-neutral-300 cursor-pointer truncate"
                                        @click="editingField = 'phone'"
                                    >{{ form.phone || '—' }}</span>
                                    <a
                                        v-if="form.phone && editingField !== 'phone'"
                                        :href="'tel:' + form.phone"
                                        class="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-neutral-400 hover:text-roori-blue hover:bg-roori-blue/10 transition flex-shrink-0"
                                        @click.stop
                                    >
                                        <i class="pi pi-phone !text-xs"></i>
                                    </a>
                                </div>
                            </div>

                            <!-- Tagy -->
                            <div class="flex flex-wrap gap-1.5 items-center px-1">
                                <template v-if="editingField !== 'tags'">
                                    <span
                                        v-for="tag in form.tags"
                                        :key="tag"
                                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-neutral-50 dark:bg-gray-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-gray-700"
                                    >
                                        <i class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ background: getTagColor(tag) }"></i>
                                        {{ tag }}
                                    </span>
                                    <button
                                        type="button"
                                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] text-neutral-400 border border-dashed border-neutral-300 dark:border-gray-600 hover:text-roori-blue hover:border-roori-blue"
                                        @click="editingField = 'tags'"
                                    >+ {{ $t('contacts.detail_tags') }}</button>
                                </template>
                                <InputChips
                                    v-else
                                    v-model="form.tags"
                                    removable
                                    class="w-full"
                                    input-class="w-full"
                                    @blur="editingField = null"
                                />
                            </div>

                            <div v-if="form.isDirty" class="flex justify-end -mt-2">
                                <Button size="small" :label="$t('common.save')" :loading="form.processing" @click="form.submit(); editingField = null" />
                            </div>
                            <div v-if="!!form.errors.email || !!form.errors.name || !!form.errors.phone" class="text-sm text-red-500 -mt-2">
                                {{ form.errors.email || form.errors.name || form.errors.phone }}
                            </div>

                            <!-- Připnutá poznámka -->
                            <FeatureBlocker name="roori_client_main_note" version="Start" inline>
                                <div
                                    v-if="noteValue && !noteEditing"
                                    class="flex gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800 cursor-pointer"
                                    @click="noteEditing = true"
                                >
                                    <i class="pi pi-bookmark-fill !text-sm text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"></i>
                                    <div class="min-w-0 flex-1 text-[13px] text-amber-800 dark:text-amber-300 leading-relaxed pinned-note-content" v-html="pinnedNoteHtml"></div>
                                </div>
                                <div v-else-if="noteEditing" class="flex flex-col gap-2">
                                    <MarkdownEditor v-model="noteValue" autosave :placeholder="$t('contacts.notes_placeholder')" @save="saveNote" />
                                    <button type="button" class="self-end text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300" @click="noteEditing = false">
                                        {{ $t('common.close') }}
                                    </button>
                                </div>
                                <button
                                    v-else
                                    type="button"
                                    class="flex items-center gap-2 p-3 rounded-xl border border-dashed border-neutral-300 dark:border-gray-600 text-neutral-400 hover:text-roori-blue hover:border-roori-blue text-sm"
                                    @click="noteEditing = true"
                                >
                                    <i class="pi pi-plus !text-xs"></i>
                                    {{ $t('contacts.note_empty_cta') }}
                                </button>
                            </FeatureBlocker>
                        </div>

                        <!-- PRAVÁ STRANA: zeď -->
                        <div class="flex-1 min-w-0 min-h-0 flex flex-col">
                            <ContactWall
                                :initial-messages="logMessages.data"
                                :initial-has-more="logMessages.current_page < logMessages.last_page"
                                :initial-next-page="logMessages.current_page + 1"
                                :contact-id="contact.id"
                                :contact="contact"
                                :credit-amount="$page.props.user.credit_amount"
                            />
                        </div>
                    </div>
                </TabPanel>
                <TabPanel value="notebook" class="!p-0 flex-1 min-h-0 flex flex-col">
                    <div class="flex-1 min-h-0 flex flex-col">
                        <ClientNotebook :contact-id="contact.id" :initial-sections="contact.notebook_sections ?? []" />
                    </div>
                </TabPanel>
            </TabPanels>
        </Tabs>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useForm } from 'laravel-precognition-vue-inertia'
import axios from 'axios'
import Button from 'primevue/button'
import InputChips from 'primevue/inputchips'
import FormInput from '../../../widget/components/ui-components/FormInput.vue'
import MarkdownEditor from '../../components/ui-components/editor/MarkdownEditor.vue'
import FeatureBlocker from '../../components/ui-components/FeatureBlocker.vue'
import ClientNotebook from '../../components/ui-components/notebook/ClientNotebook.vue'
import ContactWall from '../../components/ui-components/contacts/ContactWall.vue'
import Tabs from 'primevue/tabs'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import { useTagColors } from '../../composables/useTagColors.js'
import { markdownToHtml } from '../../../../markdown.js'

const props = defineProps({
    contact: Object,
    logMessages: Object,
})

const activeTab = ref('client')

const { getTagColor } = useTagColors()

const form = useForm('patch', '/app/contacts/' + props.contact.id, {
    name: props.contact.name || '',
    email: props.contact.email || '',
    phone: props.contact.phone || '',
    tags: props.contact.tags || [],
})

const editingField = ref(null)

const previousNamesExpanded = ref(false)
const visiblePreviousNames = computed(() => {
    const names = props.contact.previous_names ?? []
    return previousNamesExpanded.value ? names : names.slice(0, 5)
})

const noteValue = ref(props.contact.notes || '')
const noteEditing = ref(false)
const pinnedNoteHtml = computed(() => markdownToHtml(noteValue.value))

function saveNote(markdown) {
    axios.patch(`/app/contacts/${props.contact.id}/note`, { notes: markdown })
}
</script>
